import { useCallback, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { CapacitorContacts } from "@capgo/capacitor-contacts";
import { supabase } from "./supabase";
import { hashContactBatch, hashRawPhone } from "./phoneHash";

// usePhoneDirectory — directory-side flows for adding friends.
//
//   setMyPhone(rawInput)
//     Normalizes + hashes the signed-in user's phone, writes
//     profiles.phone_hash. Idempotent. Pass null/empty to opt out.
//
//   findFromContacts()
//     Native iOS path. Requests the contacts permission via
//     @capgo/capacitor-contacts (the Capacitor 8 compatible fork —
//     the community/contacts package is stuck on Cap 7 and wouldn't
//     install). Pulls every contact's phone numbers, hashes them
//     locally with the same pepper as setMyPhone, calls the
//     match_phone_hashes RPC, returns the intersection. Raw phone
//     numbers never leave the device.
//
//     Returns: [{ user_id, name, avatar_url, invite_code, contactLabel }]
//
// Status flags:
//   busy        — currently in flight
//   permission  — 'unknown' | 'granted' | 'denied' | 'limited' | 'unsupported'
//   error       — last error message (string) or null

const IS_NATIVE =
  typeof window !== "undefined"
  && Capacitor.isNativePlatform?.();

export function usePhoneDirectory(userId) {
  const [busy, setBusy] = useState(false);
  const [permission, setPermission] = useState(IS_NATIVE ? "unknown" : "unsupported");
  const [error, setError] = useState(null);

  const setMyPhone = useCallback(async (rawInput) => {
    if (!userId) return null;
    setBusy(true);
    setError(null);
    try {
      const hash = rawInput ? await hashRawPhone(rawInput) : null;
      const { error: err } = await supabase
        .from("profiles")
        .update({ phone_hash: hash })
        .eq("id", userId);
      if (err) throw err;
      return hash;
    } catch (e) {
      console.error("[phoneDirectory] setMyPhone:", e);
      setError(e?.message || "Couldn't save your phone");
      return null;
    } finally {
      setBusy(false);
    }
  }, [userId]);

  const findFromContacts = useCallback(async () => {
    if (!IS_NATIVE) {
      setError("Contact sync is iOS-only — share your invite link instead.");
      return [];
    }
    setBusy(true);
    setError(null);
    try {
      // ── permission ──────────────────────────────────────────
      const perm = await CapacitorContacts.requestPermissions({
        permissions: ["readContacts"],
      });
      const state = perm?.readContacts;
      setPermission(state || "unknown");
      if (state !== "granted" && state !== "limited") {
        setError("Contacts permission was not granted.");
        return [];
      }

      // ── read contacts ───────────────────────────────────────
      // Only ask for the fields we actually need — keeps the call
      // fast on devices with thousands of contacts.
      const { contacts } = await CapacitorContacts.getContacts({
        fields: ["phoneNumbers", "fullName", "givenName"],
      });
      const rawPhones = [];
      for (const c of contacts || []) {
        const label = c?.fullName || c?.givenName || null;
        for (const p of c?.phoneNumbers || []) {
          const num = p?.value;
          if (num) rawPhones.push({ num, label });
        }
      }
      if (rawPhones.length === 0) {
        setError("No phone numbers found in your contacts.");
        return [];
      }

      // ── hash + match ────────────────────────────────────────
      const hashed = await hashContactBatch(rawPhones.map((r) => r.num));
      const labelByHash = new Map();
      hashed.forEach((h, i) => labelByHash.set(h.hash, rawPhones[i]?.label || null));
      if (hashed.length === 0) return [];

      const { data, error: err } = await supabase.rpc("match_phone_hashes", {
        p_hashes: hashed.map((h) => h.hash),
      });
      if (err) throw err;

      return (data || [])
        .filter((row) => row.user_id !== userId)
        .map((row) => ({
          user_id:      row.user_id,
          name:         row.name,
          avatar_url:   row.avatar_url,
          invite_code:  row.invite_code,
          contactLabel: labelByHash.get(row.phone_hash) || null,
        }));
    } catch (e) {
      console.error("[phoneDirectory] findFromContacts:", e);
      setError(e?.message || "Couldn't read contacts");
      return [];
    } finally {
      setBusy(false);
    }
  }, [userId]);

  // Username search — independent of platform. Accepts a handle
  // (without the @), normalizes, hits the match_usernames RPC.
  // Returns a single match (or null) since username lookups are
  // exact, not prefix.
  const findByUsername = useCallback(async (rawHandle) => {
    if (!rawHandle) return null;
    setBusy(true);
    setError(null);
    try {
      const cleaned = String(rawHandle).trim().toLowerCase().replace(/^@/, "");
      if (!/^[a-z0-9_]{3,24}$/.test(cleaned)) {
        setError("Usernames are 3–24 lowercase letters, numbers, or underscores.");
        return null;
      }
      const { data, error: err } = await supabase.rpc("match_usernames", {
        p_handles: [cleaned],
      });
      if (err) throw err;
      const row = (data || [])[0];
      if (!row) {
        setError(`No one with @${cleaned} on Booda yet.`);
        return null;
      }
      if (row.user_id === userId) {
        setError("That's your own handle.");
        return null;
      }
      return {
        user_id:     row.user_id,
        name:        row.name,
        avatar_url:  row.avatar_url,
        invite_code: row.invite_code,
        username:    row.username,
      };
    } catch (e) {
      console.error("[phoneDirectory] findByUsername:", e);
      setError(e?.message || "Couldn't look up that username");
      return null;
    } finally {
      setBusy(false);
    }
  }, [userId]);

  return {
    busy,
    permission,
    error,
    setMyPhone,
    findFromContacts,
    findByUsername,
    nativeSupported: IS_NATIVE,
  };
}
